import { z } from 'zod';
import { CardAction } from './cards.ts';

/**
 * What a click on a message does, the same for every channel (BB-129, CARD-009): the card actions plus what in-app
 * messages need (close, go to a page, ask for push permission). `web_url` always says where the page opens
 * (`openIn: in_app | external`) — no default to guess. The SDKs hand every action to the host's single
 * `ActionHandler` first and fall back to their defaults (Custom Tabs / SFSafariViewController for `in_app`).
 */
export const MessageAction = z.discriminatedUnion('kind', [
  ...CardAction.options,
  z.strictObject({ kind: z.literal('close') }),
  z.strictObject({ kind: z.literal('go_to_page'), pageId: z.string().min(1).max(100) }),
  z.strictObject({ kind: z.literal('request_push_permission') }),
]);
export type MessageAction = z.infer<typeof MessageAction>;

export const ACTION_CHANNELS = ['content_card', 'push', 'in_app', 'banner'] as const;

/** Where an action comes from; the host's handler gets it with every action. */
export const ActionSource = z.strictObject({ channel: z.enum(ACTION_CHANNELS), messageId: z.uuid().nullable() });
export type ActionSource = z.infer<typeof ActionSource>;

/**
 * What the SDK does with an action the host did not handle (the shared fixtures `fixtures/actions/*.json`):
 * open a deep link in the app, open a web page in an in-app browser tab or the external browser, log through the SDK,
 * or leave it to the message UI (close, page change, permission prompt) or do nothing.
 */
export const DEFAULT_ACTION_OUTCOMES = ['open_deep_link', 'open_in_app_browser', 'open_external_browser', 'sdk', 'message_ui', 'nothing'] as const;
export type DefaultActionOutcome = (typeof DEFAULT_ACTION_OUTCOMES)[number];
